// A single coordinated Escape-key stack for layered UI — dropdowns, bottom sheets, modals,
// lightboxes, the coach mobile sidebar. Each layer registers a close callback while it's open;
// Escape closes only the highest-priority layer currently registered, so pressing it once never
// closes two things at once and is never swallowed by a lower-priority layer's own listener.
// ENG-33816.
//
// Dropdown and Modal already have their own working Escape mechanism, kept untouched — they
// register here only so lower-priority layers know to defer to them (this stack's capture-phase
// handler still finds and calls their real closer first; their own listener then either never
// fires, because stopPropagation() already stopped it, or is a harmless no-op if it does).
// Every other layer type (bottom sheet, mobile sidebar, lightbox) has no independent Escape
// mechanism of its own and relies entirely on this stack calling its registered closer —
// notably, a native <dialog>'s own Escape-dismiss can't be used for this: any mounted
// ModalComponent instance unconditionally preventDefault()s every Escape keydown at the window
// level regardless of whether it's open, which silently suppresses native dialog dismissal
// app-wide (confirmed empirically, not just per spec).
//
// The same stack decides where Tab focus is trapped (ENG-35498): a layer registered with
// `focusTrap: element` keeps Tab inside that element while it's the topmost layer, and gets focus
// back to whatever opened it when it closes. See focus_trap.js.
import { focusInto, focusLost, isFocusable, wrapTab } from './focus_trap.js'
import { legacyDialogAbove } from '../extensions/bootstrap_modal_layering.mjs'

const PRIORITY = [
  'dropdown',
  'bottom-sheet',
  'modal',
  'lightbox',
  'mobile-sidebar',
  'side-panel', // Non-modal: never closes on Escape, only traps Tab when nothing else is open
]

// type -> Map of close callback -> { escape, focusTrap, opener }, in registration order
const openLayers = new Map()

let listening = false

function handleKeydown(event) {
  if (event.key !== 'Escape' || event.isComposing) return

  for (const type of PRIORITY) {
    // Layers that don't close on Escape are only here for their focus trap, and Escape passes
    // them by. Priority is by type, not stacking, so a lightbox opened over such a modal still
    // closes.
    const closers = [...(openLayers.get(type) || [])]
      .filter(([, layer]) => layer.escape)
      .map(([close]) => close)
    if (closers.length === 0) continue

    const close = closers.at(-1) // most-recently-opened wins within the same type
    // A held key auto-repeats. One press must close one layer, never cascade
    // through the ones underneath (e.g. a link picker's panes, then its modal).
    if (event.repeat) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    // A closer returns false to decline: something that isn't on this stack (a Bootstrap
    // modal, a TinyMCE dialog) sits above it and handles Escape itself. Leave the event alone
    // and don't fall through to lower layers either.
    if (close(event) === false) return

    event.preventDefault()
    event.stopPropagation()
    return
  }
}

// The latest trapping layer of the highest-priority type that has one
function latestTrap() {
  for (const type of PRIORITY) {
    const trap = [...(openLayers.get(type)?.values() || [])].findLast(
      (layer) => layer.focusTrap
    )
    if (trap) return trap
  }
  return null
}

// The layer whose focus trap is live for Tab. A higher open layer without a trap (a dropdown, a
// bottom sheet) suspends trapping while it's open.
function topmostTrap() {
  for (const type of PRIORITY) {
    const layers = openLayers.get(type)
    if (!layers || layers.size === 0) continue

    return [...layers.values()].findLast((layer) => layer.focusTrap) || null
  }
  return null
}

function dialogModalOpen() {
  try {
    return !!document.querySelector('dialog:modal')
  } catch {
    return false // :modal unsupported
  }
}

// Bubble phase, unlike Escape: a component that uses Tab itself (editor suggestion menus, the
// link picker) preventDefaults it on the way up, and then the trap leaves it alone.
function handleTab(event) {
  if (
    event.key !== 'Tab' ||
    event.defaultPrevented ||
    event.ctrlKey ||
    event.altKey ||
    event.metaKey
  )
    return

  const layer = topmostTrap()
  // A native modal <dialog> (lightboxes) or a Bootstrap/TinyMCE dialog above us owns focus
  if (!layer || dialogModalOpen() || legacyDialogAbove(document)) return

  wrapTab(event, layer.focusTrap, {
    opener: layer.blocksPage ? layer.opener : null,
  })
}

function ensureListening() {
  if (listening) return
  listening = true
  // Capture phase: run before any other document/window-level keydown listener, so a lower-
  // priority layer's own (untouched) handler never gets a chance to act first.
  document.addEventListener('keydown', handleKeydown, true)
  document.addEventListener('keydown', handleTab)
}

// Where focus was when a trapping layer opened: something outside it to return to on close
function openerFor(focusTrap) {
  const active = document.activeElement
  if (focusLost(active) || focusTrap.contains(active)) return null
  return active
}

// `escape: false` — the layer doesn't close on Escape, and Escape passes it by (see handleKeydown).
// `focusTrap` — element to keep Tab inside while this is the topmost layer.
export function registerEscLayer(
  type,
  close,
  { escape = true, focusTrap = null } = {}
) {
  if (!PRIORITY.includes(type)) {
    throw new Error(`esc_stack: unknown layer type "${type}"`)
  }
  ensureListening()
  if (!openLayers.has(type)) openLayers.set(type, new Map())
  const layers = openLayers.get(type)

  const existing = layers.get(close)
  if (existing) {
    // Re-registered while open (e.g. side panel content swapped): keeps its place in the order,
    // and its opener unless a new one is at hand
    const opener = focusTrap && openerFor(focusTrap)
    Object.assign(existing, {
      escape,
      focusTrap,
      opener: opener || existing.opener,
    })
    return
  }
  layers.set(close, {
    escape,
    focusTrap,
    blocksPage: type !== 'side-panel', // the side panel leaves the page usable
    opener: focusTrap && openerFor(focusTrap),
  })
  announceBlockingChange()
}

export function unregisterEscLayer(type, close) {
  const layers = openLayers.get(type)
  const layer = layers?.get(close)
  if (!layer) return

  // Topmost among traps even if a dropdown is still open above it (hidden programmatically)
  const wasTopmost = layer.focusTrap && latestTrap() === layer
  layers.delete(close)
  announceBlockingChange()
  if (!layer.focusTrap) return

  if (!wasTopmost) {
    // Closing underneath a layer it opened (a confirm whose callback opened a prompt): that layer's
    // opener sits inside this one, which is about to go away, so it returns focus to where this
    // one came from instead
    for (const other of [...openLayers.values()].flatMap((l) => [
      ...l.values(),
    ])) {
      if (other.opener && layer.focusTrap.contains(other.opener))
        other.opener = layer.opener
    }
    return
  }

  const active = document.activeElement
  if (!focusLost(active) && !layer.focusTrap.contains(active)) return

  if (layer.opener && isFocusable(layer.opener)) {
    layer.opener.focus({ preventScroll: true })
  } else {
    const next = latestTrap()
    if (next) focusInto(next.focusTrap, { first: false })
  }
}

// Fires `esc-stack:blocking-changed` on document when the page goes from "nothing layered" to
// "something layered" or back, with `detail.blocking`. The Swift Coach app (simplero-coach-ios)
// listens to give an open menu, sheet or modal the whole screen, over its native composer.
let announcedBlocking = false
function announceBlockingChange() {
  const blocking = hasAnyOpenLayer()
  if (blocking === announcedBlocking) return
  announcedBlocking = blocking
  // Optional call: unit tests run this against minimal fake documents.
  document.dispatchEvent?.(
    new CustomEvent('esc-stack:blocking-changed', { detail: { blocking } })
  )
}

// True when any layer that blocks the page is open — used by the keyboard-shortcuts controller to
// suppress global (non-Esc) bindings while something layered is up. The side panel doesn't count.
export function hasAnyOpenLayer() {
  return [...openLayers.entries()].some(
    ([type, layers]) => type !== 'side-panel' && layers.size > 0
  )
}
